/**
 * Card — visual representation of a playing card.
 *
 * Face-up: white background with rank + suit glyph (red for hearts/diamonds,
 * black for spades/clubs) per /design/specs/palette.md §5.
 * Face-down: brand-purple "card back" per the same palette.
 *
 * Size variants:
 *   sm — small cards (e.g., action history thumbnails)
 *   md — default (board cards, hole cards on resolution screen)
 *   lg — larger (hero's hole cards on Active Hand)
 */

import type { Card } from '@/types/card';
import { cn } from '@/lib/utils';

export type CardSize = 'sm' | 'md' | 'lg' | 'xl';

interface CardViewProps {
  card?: Card;
  faceDown?: boolean;
  size?: CardSize;
  className?: string;
}

const SUIT_GLYPH: Record<string, string> = {
  s: '♠',
  h: '♥',
  d: '♦',
  c: '♣',
};

const SIZE_STYLES: Record<CardSize, string> = {
  sm: 'h-10 w-7 text-xs',
  md: 'h-14 w-10 text-sm',
  lg: 'h-20 w-14 text-base',
  xl: 'h-28 w-20 text-2xl',
};

const SUIT_COLOR_CLASS: Record<string, string> = {
  s: 'text-suit-spade',
  c: 'text-suit-club',
  h: 'text-suit-heart',
  d: 'text-suit-diamond',
};

export function CardView({
  card,
  faceDown = false,
  size = 'md',
  className,
}: CardViewProps) {
  if (faceDown || !card) {
    return (
      <div
        className={cn(
          'rounded-md border border-border-strong',
          'flex items-center justify-center',
          SIZE_STYLES[size],
          'bg-surface-card-back',
          className,
        )}
        aria-label="Face-down card"
      />
    );
  }

  const suitClass = SUIT_COLOR_CLASS[card.suit] ?? 'text-text-on-light';

  return (
    <div
      className={cn(
        'rounded-md border border-border',
        'flex flex-col items-center justify-center',
        'bg-surface-card-face shadow-sm',
        SIZE_STYLES[size],
        className,
      )}
      aria-label={`${card.rank} of ${card.suit}`}
    >
      <span className={cn('font-bold leading-none', suitClass)}>{card.rank}</span>
      <span className={cn('leading-none', suitClass)}>{SUIT_GLYPH[card.suit]}</span>
    </div>
  );
}

/** Convenience: render an array of cards in a row. */
export function CardsRow({
  cards,
  faceDown = false,
  size = 'md',
  className,
}: {
  cards?: Card[];
  count?: number;
  faceDown?: boolean;
  size?: CardSize;
  className?: string;
}) {
  if (!cards) return null;
  return (
    <div className={cn('flex gap-1', className)}>
      {cards.map((c, i) => (
        <CardView key={`${c.rank}${c.suit}-${i}`} card={c} faceDown={faceDown} size={size} />
      ))}
    </div>
  );
}

/** Convenience: render N face-down cards (when villain hand is hidden). */
export function FaceDownCards({
  count,
  size = 'md',
  className,
}: {
  count: number;
  size?: CardSize;
  className?: string;
}) {
  return (
    <div className={cn('flex gap-1', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <CardView key={i} faceDown size={size} />
      ))}
    </div>
  );
}
